import { useId, useState, type FormEvent, type ReactNode } from 'react';

import { Botao, type NivelBotao } from '@/componentes/Botao';
import { CampoTexto } from '@/componentes/Campo';
import { JanelaDeAcao } from '@/componentes/JanelaDeAcao';
import { formatarDataHora } from '@/lib/formato';
import { primeiraMaiuscula } from '@/lib/texto';

/**
 * Peças da administração da plataforma, repetidas entre as seções.
 */

export function Recado({ erro }: { readonly erro: { readonly erro: string; readonly comoResolver: string } }) {
  return (
    <div role="alert" className="flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-4">
      <span className="txt-corpo text-tinta">{primeiraMaiuscula(erro.erro)}</span>
      <span className="txt-apoio text-tinta-secundaria">{erro.comoResolver}</span>
    </div>
  );
}

export function Bloco({ titulo, children, acoes }: { readonly titulo: string; readonly children: ReactNode; readonly acoes?: ReactNode }) {
  return (
    <section className="cartao flex flex-col gap-4 p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="txt-secao text-tinta">{titulo}</h2>
        {acoes ? <div className="flex items-center gap-2">{acoes}</div> : null}
      </div>
      {children}
    </section>
  );
}

/**
 * Ato que exige MOTIVO: abre a janela, pede a frase e só então confirma. O
 * motivo vai para a trilha da plataforma e para a da empresa — é o que quem
 * administra a empresa lê depois para entender o que a plataforma fez nela.
 *
 * A janela é a comum (R01, p.29): ao centro, 480–640 px, fundo escurecido,
 * "✕" e `Esc` fecham, cabeçalho fixo, e em tela cheia abaixo de 768 px. O
 * título é o próprio ato; a pergunta e a consequência abrem o corpo.
 */
export function AcaoComMotivo({
  rotulo,
  nivel = 'secundario',
  pergunta,
  consequencia,
  rotuloConfirmar,
  pendente = false,
  ajudaDoMotivo = 'Vai para a trilha da plataforma e para a da empresa.',
  aoConfirmar,
}: {
  readonly rotulo: string;
  readonly nivel?: NivelBotao;
  readonly pergunta: string;
  readonly consequencia: string;
  readonly rotuloConfirmar: string;
  readonly pendente?: boolean;
  /** Para onde o motivo vai. O ato GLOBAL (a IA, 0042) só tem a trilha da plataforma. */
  readonly ajudaDoMotivo?: string;
  readonly aoConfirmar: (motivo: string) => void;
}) {
  const [aberta, setAberta] = useState(false);

  return (
    <JanelaDeAcao
      rotulo={rotulo}
      titulo={rotulo}
      nivel={nivel}
      desligado={pendente}
      aberta={aberta}
      aoAbrir={() => {
        setAberta(true);
      }}
      aoFechar={() => {
        setAberta(false);
      }}
    >
      <PedidoDeMotivo
        pergunta={pergunta}
        consequencia={consequencia}
        rotuloConfirmar={rotuloConfirmar}
        destrutivo={nivel === 'destrutivo'}
        ajuda={ajudaDoMotivo}
        aoCancelar={() => {
          setAberta(false);
        }}
        aoConfirmar={(motivo) => {
          setAberta(false);
          aoConfirmar(motivo);
        }}
      />
    </JanelaDeAcao>
  );
}

/** O corpo da janela do ato: montado a cada abertura, então o motivo começa sempre em branco. */
function PedidoDeMotivo({
  pergunta,
  consequencia,
  rotuloConfirmar,
  destrutivo,
  ajuda,
  aoCancelar,
  aoConfirmar,
}: {
  readonly pergunta: string;
  readonly consequencia: string;
  readonly rotuloConfirmar: string;
  readonly destrutivo: boolean;
  readonly ajuda: string;
  readonly aoCancelar: () => void;
  readonly aoConfirmar: (motivo: string) => void;
}) {
  const [motivo, setMotivo] = useState('');
  const vazio = motivo.trim() === '';

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    if (vazio) return;
    aoConfirmar(motivo.trim());
  }

  return (
    <form className="flex flex-col gap-4" onSubmit={enviar}>
      <div className="flex flex-col gap-2">
        <p className="txt-subtitulo text-tinta">{pergunta}</p>
        <p className="txt-corpo text-tinta-secundaria">{consequencia}</p>
      </div>
      <CampoTexto
        rotulo="Motivo"
        valor={motivo}
        maxLength={300}
        ajuda={ajuda}
        validar={(v) => (v.trim() === '' ? 'Escreva o motivo: sem ele o ato não fica explicado na trilha.' : null)}
        aoMudar={setMotivo}
      />
      {/* Barra de ações (R01): a de voltar à esquerda, a do ato à direita. */}
      <div className="barra-de-acoes">
        <Botao nivel="secundario" onClick={aoCancelar}>
          Manter como está
        </Botao>
        <Botao nivel={destrutivo ? 'destrutivo' : 'secundario'} type="submit" disabled={vazio}>
          {rotuloConfirmar}
        </Botao>
      </div>
    </form>
  );
}

/**
 * O endereço de um convite, que aparece UMA vez: o banco guarda só o hash do
 * código. Quem administra copia e entrega; a pessoa escolhe a senha.
 */
export function EnderecoParaEntregar({
  titulo,
  endereco,
  para,
  expiraEm,
  depois,
  aoFechar,
}: {
  readonly titulo: string;
  readonly endereco: string;
  readonly para: string;
  readonly expiraEm: string;
  readonly depois: string;
  readonly aoFechar: () => void;
}) {
  const base = useId();
  const [copiado, setCopiado] = useState(false);
  return (
    <div role="status" className="flex flex-col gap-3 rounded-controle border border-borda bg-rebaixada p-4">
      <span className="txt-subtitulo text-tinta">{titulo}</span>
      <label htmlFor={`${base}-endereco`} className="txt-rotulo-campo">
        Endereço do convite
      </label>
      <div className="flex items-center gap-2">
        <input
          id={`${base}-endereco`}
          readOnly
          className="controle codigo min-w-0 flex-1"
          value={endereco}
          onFocus={(e) => {
            e.target.select();
          }}
        />
        <Botao
          nivel="secundario"
          onClick={() => {
            void navigator.clipboard?.writeText(endereco).then(() => {
              setCopiado(true);
            });
          }}
        >
          {copiado ? 'Endereço copiado' : 'Copiar endereço'}
        </Botao>
      </div>
      <p className="txt-apoio text-tinta-secundaria">
        {`Entregue a ${para}. Ele aparece só agora, vale uma vez e até ${formatarDataHora(new Date(expiraEm))}. ${depois}`}
      </p>
      <div className="flex justify-end">
        <Botao nivel="terciario" onClick={aoFechar}>
          Fechar o endereço
        </Botao>
      </div>
    </div>
  );
}
